import React from 'react';
import Link from 'next/link';
import { Logo } from '@/components/logo';
import { Twitter, Linkedin, Mail, Shield, Lock, CreditCard, CheckCircle2 } from 'lucide-react';
import { TrustBadge } from '@/components/marketing';

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-background border-t border-border">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-8 mb-8">
          {/* Brand Column */}
          <div className="col-span-1 md:col-span-2 lg:col-span-2">
            <Logo size="sm" />
            <p className="mt-1 text-xs text-muted-foreground/70 font-medium tracking-wide uppercase">
              AI-powered inbox defense
            </p>
            <p className="mt-4 text-muted-foreground text-sm max-w-xs leading-relaxed">
              Stop AI-generated sales emails from cluttering your Gmail. Email Ferret catches the
              cold outreach that Gmail misses&mdash;automatically.
            </p>
            <div className="flex gap-4 mt-6">
              <a
                href="https://twitter.com/email_ferret"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/110188068"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="mailto:support@emailferret.io"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Email"
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Product Column */}
          <div>
            <h3 className="font-semibold mb-4">Product</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/features"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Features
                </Link>
              </li>
              <li>
                <Link
                  href="/how-it-works"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  How It Works
                </Link>
              </li>
              <li>
                <Link
                  href="/pricing"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Pricing
                </Link>
              </li>
              <li>
                <Link
                  href="/reviews"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Reviews
                </Link>
              </li>
              <li>
                <Link
                  href="/login"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Get Started
                </Link>
              </li>
            </ul>
          </div>

          {/* Solutions Column */}
          <div>
            <h3 className="font-semibold mb-4">Solutions</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/gmail-spam-filter"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Gmail Spam Filter
                </Link>
              </li>
              <li>
                <Link
                  href="/block-cold-emails"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Block Cold Emails
                </Link>
              </li>
              <li>
                <Link
                  href="/ai-spam-detection-gmail"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  AI Spam Detection
                </Link>
              </li>
              <li>
                <Link
                  href="/stop-sales-emails"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Stop Sales Emails
                </Link>
              </li>
              <li>
                <Link
                  href="/auto-label-gmail"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Auto-Label Gmail
                </Link>
              </li>
              <li>
                <Link
                  href="/email-allowlist"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Email Allowlist
                </Link>
              </li>
              <li>
                <Link
                  href="/email-blocklist"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Email Blocklist
                </Link>
              </li>
              <li>
                <Link
                  href="/block-spam-from"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Block Spam by Platform
                </Link>
              </li>
            </ul>
          </div>

          {/* Use Cases Column */}
          <div>
            <h3 className="font-semibold mb-4">Use Cases</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/use-cases"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  All Use Cases
                </Link>
              </li>
              <li>
                <Link
                  href="/use-cases/executives"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  For Executives
                </Link>
              </li>
              <li>
                <Link
                  href="/use-cases/founders"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  For Founders
                </Link>
              </li>
              <li>
                <Link
                  href="/use-cases/recruiters"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  For Recruiters
                </Link>
              </li>
              <li>
                <Link
                  href="/use-cases/engineers"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  For Engineers
                </Link>
              </li>
              <li>
                <Link
                  href="/use-cases/salespeople"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  For Sales Teams
                </Link>
              </li>
              <li>
                <Link
                  href="/use-cases/lawyers"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  For Lawyers
                </Link>
              </li>
            </ul>
          </div>

          {/* Compare Column */}
          <div>
            <h3 className="font-semibold mb-4">Compare</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/compare"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  All Comparisons
                </Link>
              </li>
              <li>
                <Link
                  href="/compare/sanebox"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  vs SaneBox
                </Link>
              </li>
              <li>
                <Link
                  href="/compare/superhuman"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  vs Superhuman
                </Link>
              </li>
              <li>
                <Link
                  href="/compare/clean-email"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  vs Clean Email
                </Link>
              </li>
              <li>
                <Link
                  href="/compare/hey"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  vs HEY
                </Link>
              </li>
              <li>
                <Link
                  href="/compare/alfred"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  vs alfred_
                </Link>
              </li>
              <li>
                <Link
                  href="/compare/gmail"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  vs Gmail Filters
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h3 className="font-semibold mb-4">Resources</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/reports"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Email Reports
                </Link>
              </li>
              <li>
                <Link
                  href="/guides"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Guides
                </Link>
              </li>
              <li>
                <Link
                  href="/templates"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Templates
                </Link>
              </li>
              <li>
                <Link
                  href="/blog"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  href="/glossary"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Glossary
                </Link>
              </li>
              <li>
                <Link
                  href="/search"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Search
                </Link>
              </li>
              <li>
                <Link
                  href="/about"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  href="/security"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Security
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Column */}
          <div>
            <h3 className="font-semibold mb-4">Support</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/faq"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  FAQ
                </Link>
              </li>
              <li>
                <a
                  href="mailto:support@emailferret.io"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  support@emailferret.io
                </a>
              </li>
              <li>
                <Link
                  href="/contact"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Contact Form
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="border-t border-border pt-8 mb-8">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
            <TrustBadge icon={CheckCircle2} label="SSL Secured" color="green" />
            <TrustBadge icon={Shield} label="GDPR Compliant" color="blue" />
            <TrustBadge icon={CreditCard} label="Secure Billing" color="purple" />
            <TrustBadge icon={Lock} label="Secure Payments" color="green" />
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-border pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-muted-foreground text-sm" suppressHydrationWarning>
              © {currentYear} Email Ferret. All rights reserved.
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-muted-foreground text-sm">
              <Link href="/security" className="hover:text-primary transition-colors">
                Security
              </Link>
              <Link href="/privacy" className="hover:text-primary transition-colors">
                Privacy
              </Link>
              <Link href="/terms" className="hover:text-primary transition-colors">
                Terms
              </Link>
              <span className="text-muted-foreground">Made with ❤️ for a cleaner inbox</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
